import React, { useRef, useState, useEffect } from 'react';
import 'highlight.js/styles/github.css'; // Import a syntax highlighting style
import hljs from 'highlight.js/lib/core';
import sql from 'highlight.js/lib/languages/sql';
import axios from 'axios';

import '../css/Editor.css';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/theme-github'; // You can choose any theme you prefer
import { getToken } from '../../../utils/TokenUtils';
import { API_URL, ENDPOINTS } from '../../../constants/api';

// Register SQL language for syntax highlighting
hljs.registerLanguage('sql', sql);

const SqlEditor = () => {
  const [query, setQuery] = useState(''); // Query input state
  const [result, setResult] = useState(null); // Query result state
  const [rowsAffected, setRowsAffected] = useState(null); // Rows affected for non-select queries
  const [error, setError] = useState(null); // Error state
  const [loading, setLoading] = useState(null); // Error state
  const [counter, setCounter] = useState(1); // Start counter from 1

  const [isNestedArray, setIsNestedArray] = useState(false);
  const token = getToken();
  const intervalRef = useRef(null); // Store interval ID

  const handleChange = (newSql) => {
    setQuery(newSql);
  };

  // Function to execute query
  const executeQuery = async () => {
    try {
      setLoading(true);

      setCounter(1); // Reset counter

      // Start a new interval
      intervalRef.current = setInterval(() => {
        setCounter((prev) => prev + 1); // Increment counter each second
        console.log('Interval running', counter); // Debugging: check if counter updates
      }, 1000);

      setError(null);
      setResult(null);
      setRowsAffected(null);

      // Mock API call to execute query (replace with your API endpoint)
      const response = await axios.post(
        `${API_URL}/${ENDPOINTS.SQL}/execute-query`,
        { query },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );
      const { data } = response;
      // Clear the previous interval if it exists
      if (intervalRef.current) {
        console.log('Clearing old interval'); // Debugging
        clearInterval(intervalRef.current);
      }
      //  console.log('data:', data);

      // Check if the response includes rows or just metadata
      if (Array.isArray(data) && data.length > 0) {
        const isNestedArray = data.some((data) => Array.isArray(data));
        setIsNestedArray(isNestedArray);
        setResult(data); // Handle SELECT query results
      } else if (data.affectedRows !== undefined) {
        setRowsAffected(data.affectedRows); // Handle INSERT, UPDATE, DELETE
      } else {
        setResult([]); // No data returned (e.g., empty SELECT result)
      }
      if (data.error) {
        setError(data.error);
      }
      setLoading(false);
    } catch (err) {
      // Handle errors
      setLoading(false);
      setError(
        err.response?.data?.message || `Failed to execute query. ${err.message}`
      );
    }
  };

  //   console.log('result:', result);

  const listStyle = {
    listStyleType: 'none', // Remove default list item style (bullets, numbers)
    padding: 0, // Remove default padding
    margin: 0, // Remove default margin
  };

  return (
    <div className="mt-5" style={{ background: '#f0f0f0' }}>
      <h2 className="mb-4">SQL Query Executor</h2>
      <div style={{ width: '95%', height: '400px', margin: '10px' }}>
        <AceEditor
          mode="sql"
          theme="github"
          onChange={handleChange}
          name="sql-editor"
          value={query}
          editorProps={{ $blockScrolling: true }}
          setOptions={{
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: true,
            enableSnippets: true,
            wrap: true, // Enables word wrap
            useWorker: false, // Prevents syntax checking warnings
          }}
          width="100%"
          height="100%"
        />
      </div>
      {/* <SqlEditor /> */}

      <div className="d-flex">
        {' '}
        <button
          className="btn btn-primary btn-sm ml-auto"
          onClick={executeQuery}
          disabled={loading}
        >
          {loading ? `Loading... ${counter} sec` : 'Execute Query'}
        </button>
      </div>

      {result && Array.isArray(result) && result.length > 0 && (
        <div className="mt-4">
          <h2>Query Results</h2>
          {isNestedArray ? (
            result.map((result, index) => {
              if (Array.isArray(result)) {
                return (
                  <div key={index} className="mb-4">
                    <h4>
                      Result Set {index + 1} (Array) - {result.length} rows
                    </h4>
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            {Object.keys(result[0] || {}).map((key) => (
                              <th key={key}>{key}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {result.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {Object.values(row).map((value, colIndex) => (
                                <td key={colIndex}>
                                  <pre className="text-wrap">
                                    {value !== null ? value : 'NULL'}
                                  </pre>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              } else {
                // For non-array results
                return (
                  <div key={index} className="mb-4">
                    <h4>Result Set {index + 1}</h4>
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            {Object.keys(result).map((key) => (
                              <th key={key}>{key}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {Object.values(result).map((value, colIndex) => (
                              <td key={colIndex}>
                                {value !== null ? value : 'NULL'}
                                <pre className="text-wrap">
                                  {value !== null ? value : 'NULL'}
                                </pre>
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              }
            })
          ) : (
            <div className="mb-4">
              <h4>Single Result - {result.length} rows</h4>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      {Object.keys(result[0] || {}).map((key) => (
                        <th key={key}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {Object.values(row).map((value, colIndex) => (
                          <td key={colIndex}>
                            <pre className="text-wrap">
                              {value !== null ? value : 'NULL'}
                            </pre>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {rowsAffected !== null && (
        <div className="mt-4">
          <h4>Query Executed Successfully</h4>
          <p>{rowsAffected} row(s) affected.</p>
        </div>
      )}

      {result &&
        (!Array.isArray(result) || result.length === 0) &&
        rowsAffected === null && (
          <div className="mt-4">
            <h4>No data returned from the query.</h4>
          </div>
        )}

      {error && (
        <div className="mt-4 text-danger">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );
};

export default SqlEditor;
